import { fhirClients } from "src-next/shared/api/fhirClient";
import { isAffiliationActive } from "../helpers/affiliationReferences";
import { organizationIdFromReference } from "../../services/resolveOrganizationDisplays";
import {
  buildTeleconsultoriaAffiliation,
  sortAffiliationRows,
  toAffiliationRow,
} from "../transformers/affiliationTransformers";
import type {
  AffiliationOrganization,
  TeleconsultoriaAffiliationRow,
} from "../types";
import { assertCanAffiliate } from "./affiliationRules";
import {
  AFFILIATION_RESOURCE_TYPE,
  searchProvidedAffiliations,
  searchReceivedAffiliations,
} from "./affiliationSearchService";

/** Organizações atendidas pela prestadora (ativas e inativas). */
export async function listProvidedAffiliations(
  providerToken: string,
): Promise<TeleconsultoriaAffiliationRow[]> {
  if (!providerToken) return [];
  const { affiliations, organizationsById } =
    await searchProvidedAffiliations(providerToken);
  const rows = affiliations
    .map((affiliation) => toAffiliationRow(affiliation, organizationsById))
    .filter((row): row is TeleconsultoriaAffiliationRow => Boolean(row));
  return sortAffiliationRows(rows);
}

/**
 * Afiliação ATIVA em que a organização é receptora (no máximo 1 pela regra).
 * Usado pela visão da receptora (fase da tela de Teleconsultoria).
 */
export async function findReceivedAffiliation(
  receiverToken: string,
): Promise<fhir5.OrganizationAffiliation | undefined> {
  if (!receiverToken) return undefined;
  const { affiliations } = await searchReceivedAffiliations(receiverToken);
  return affiliations.find(isAffiliationActive);
}

export interface ReceivedProvider {
  affiliation: fhir5.OrganizationAffiliation;
  /** `<oid>-<cnpj>` da prestadora. */
  providerToken: string;
  providerName: string;
}

/**
 * Prestadora da organização receptora (afiliação HIE/HIO ativa).
 * Nome: Organization incluída no bundle → display gravado na afiliação.
 * Erro de busca é propagado: quem chama decide o estado de erro (sem fallback).
 */
export async function findReceivedProvider(
  receiverToken: string,
): Promise<ReceivedProvider | undefined> {
  if (!receiverToken) return undefined;
  const { affiliations, organizationsById } =
    await searchReceivedAffiliations(receiverToken);
  const affiliation = affiliations.find(isAffiliationActive);
  const providerToken = organizationIdFromReference(affiliation?.organization?.reference);
  if (!affiliation || !providerToken) return undefined;
  return {
    affiliation,
    providerToken,
    providerName:
      organizationsById.get(providerToken)?.name ||
      affiliation.organization?.display ||
      "organização prestadora",
  };
}

/** Valida as regras e grava uma nova afiliação HIE/HIO. */
export async function createAffiliation(params: {
  provider: AffiliationOrganization;
  receiver: AffiliationOrganization;
}): Promise<fhir5.OrganizationAffiliation> {
  await assertCanAffiliate({
    providerToken: params.provider.token,
    receiverToken: params.receiver.token,
    isNewPair: true,
  });
  return fhirClients.admin.create<fhir5.OrganizationAffiliation>(
    AFFILIATION_RESOURCE_TYPE,
    buildTeleconsultoriaAffiliation(params),
  );
}

/**
 * Ativa/desativa a afiliação.
 * Reativar passa pelas mesmas regras (ex.: a receptora pode ter sido
 * afiliada a outra prestadora enquanto esta estava inativa).
 */
export async function setAffiliationActive(params: {
  affiliation: fhir5.OrganizationAffiliation;
  isActive: boolean;
}): Promise<void> {
  const { affiliation, isActive } = params;
  if (!affiliation.id) throw new Error("Afiliação sem id");

  if (isActive) {
    await assertCanAffiliate({
      providerToken: organizationIdFromReference(affiliation.organization?.reference),
      receiverToken: organizationIdFromReference(
        affiliation.participatingOrganization?.reference,
      ),
      ignoreAffiliationId: affiliation.id,
      isNewPair: false,
    });
  }

  // JSON Patch "add" cria ou substitui o campo, então serve com ou sem `active` gravado.
  await fhirClients.admin.patch<fhir5.OrganizationAffiliation>(
    AFFILIATION_RESOURCE_TYPE,
    affiliation.id,
    [{ op: "add", path: "/active", value: isActive }],
  );
}
